import { useState } from 'react'
import { CalendarCheck2, CalendarDays, Lock } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CalendarError } from './googleCalendar'
import { useCalendarConnection } from './useCalendar'

/** Profile menu > Google Calendar: connect, reconnect or disconnect, with what we read stated plainly. */
export function GoogleCalendarDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { status, lapsed, email, connectedAt, connect, disconnect } = useCalendarConnection()
  const [busy, setBusy] = useState(false)
  const run = async () => {
    setBusy(true)
    try {
      await connect()
      toast.success('Google Calendar connected', { description: 'It stays connected until you disconnect it.' })
    } catch (err) {
      if (err instanceof CalendarError && err.kind === 'retry') toast(err.message, { action: { label: 'Connect', onClick: () => void run() } })
      else toast.error(err instanceof CalendarError ? err.message : 'Could not connect Google Calendar.')
    } finally {
      setBusy(false)
    }
  }
  const unlink = async () => {
    setBusy(true)
    try {
      await disconnect()
      toast('Google Calendar disconnected')
    } catch {
      toast.error('Could not disconnect. Try again.')
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarDays className="size-5 text-brand" /> Google Calendar</DialogTitle>
          <DialogDescription>Show your events in Calendar, get reminders in the Dynamic Island, and join calls in one click.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-border p-3 text-sm">
          <p className="flex items-center gap-2 font-medium">
            {status === 'connected' ? <CalendarCheck2 className="size-4 text-success" /> : <CalendarDays className="size-4 text-text-muted" />}
            {status === 'connected' ? `Connected${email ? ` as ${email}` : ''}` : status === 'unavailable' ? 'Unavailable right now' : status === 'loading' ? 'Checking…' : 'Not connected'}
          </p>
          {status === 'disconnected' && lapsed ? (
            <p className="mt-1 text-xs text-warning">Google ended the link — it was revoked or expired. Connect again to bring your events back.</p>
          ) : null}
          {status === 'connected' && connectedAt ? (
            <p className="mt-1 text-xs text-text-muted">Since {new Date(connectedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })} · stays connected on every device</p>
          ) : null}
          <p className="mt-2 flex gap-2 text-xs text-text-secondary">
            <Lock className="mt-0.5 size-3.5 shrink-0" />
            Read-only. Connect once and it stays connected on every browser and device until you disconnect here or remove TapWork from your Google account. TapWork reads events when you look at them and never stores or changes them.
          </p>
        </div>
        <DialogFooter className="gap-2">
          {status === 'connected' ? (
            <Button variant="outline" onClick={() => void unlink()} disabled={busy}>Disconnect</Button>
          ) : (
            <Button onClick={() => void run()} disabled={busy || status !== 'disconnected'}>{busy ? 'Opening Google…' : 'Connect'}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Small status chip for the Calendar page header. */
export function GoogleCalendarStatus() {
  const { status, email } = useCalendarConnection()
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <span className={status === 'connected' ? 'size-2 rounded-full bg-success' : status === 'unavailable' ? 'size-2 rounded-full bg-warning' : 'size-2 rounded-full bg-text-muted'} />
        {status === 'connected' ? (email ?? 'Google Calendar') : status === 'loading' ? 'Google Calendar' : 'Connect Google Calendar'}
      </Button>
      {open ? <GoogleCalendarDialog open onOpenChange={setOpen} /> : null}
    </>
  )
}
